import { z } from "zod";

const id = z.string().min(1).max(256);
const instant = z.iso.datetime({ offset: true });

/** Owner-approved exception for exactly the initial pilot's existing processor arrangement. */
export const PilotAnswerFinderWaiverSchema = z.strictObject({
  kind: z.literal("owner_pilot_waiver"),
  approvedBy: z.string().min(1).max(200),
  approvedAt: instant,
  evidence: z.string().min(1).max(300),
  providerDpaAbsent: z.literal(true),
  approvedWorkspaceIds: z.array(z.string().min(1).max(200)).length(3),
});

const AnswerFinderQuestionSchema = z.strictObject({
  id,
  key: z.literal("approved_facts"),
  question: z.string().min(1).max(2_000),
});

const AnswerFinderPassageSchema = z.strictObject({
  id,
  text: z.string().min(1).max(4_000),
  authoredAt: instant,
  sourceKind: z.enum(["seller_reply", "approved_context", "rep_answer"]),
});

export const AnswerFinderInputSchema = z.strictObject({
  questions: z.array(AnswerFinderQuestionSchema).min(1).max(20),
  passages: z.array(AnswerFinderPassageSchema).max(40),
});
export type AnswerFinderInput = z.infer<typeof AnswerFinderInputSchema>;

const AnswerFinderMatchSchema = z.strictObject({
  passageId: id,
  quote: z.string().min(1).max(4_000),
  decision: z.enum(["yes", "no"]),
  completeness: z.enum(["full", "partial"]),
});

export const AnswerFinderOutputSchema = z.strictObject({
  answers: z
    .array(
      z.strictObject({
        questionId: id,
        matches: z.array(AnswerFinderMatchSchema).max(5),
      }),
    )
    .max(20),
});
export type AnswerFinderOutput = z.infer<typeof AnswerFinderOutputSchema>;

/**
 * The only answer-finder evidence persisted on a rep-facing plan.
 *
 * The quote is deliberately absent. It is rendered from the current source message, so deleting
 * that message removes the personal text from the card without chasing copied strings through
 * plans, events, stage artifacts or backups.
 */
export const AnswerFinderSuggestionSchema = z.strictObject({
  decision: z.enum(["yes", "no"]),
  source: z.strictObject({
    messageId: id,
    offsets: z.strictObject({
      start: z.number().int().nonnegative(),
      end: z.number().int().positive(),
    }),
  }),
});
export type AnswerFinderSuggestion = z.infer<typeof AnswerFinderSuggestionSchema>;

/** Ephemeral API view, reconstructed from the source snapshot on every read. */
export const AnswerFinderSourceViewSchema = z.strictObject({
  questionId: id,
  messageId: id,
  offsets: z.strictObject({
    start: z.number().int().nonnegative(),
    end: z.number().int().positive(),
  }),
  quote: z.string().min(1).max(4_000),
  authoredAt: instant,
  source: z.literal("Earlier seller reply in this campaign"),
});
export type AnswerFinderSourceView = z.infer<typeof AnswerFinderSourceViewSchema>;
